import React, { useState, useEffect } from 'react';
import { Form, Select, Input, InputNumber, Button, Row, Col, Spin, message, Divider, Switch } from 'antd';
import AgronomistAdminWrapper from './AgronomistAdmin.styles';
import { sprayService } from '@iso/services';
import { useSelector } from 'react-redux';
import {
  SPRAY_REQUIRED_SECTIONS,
  mergeRequiredSections,
} from '@iso/constants/sprayRequiredSections';

const { Option } = Select;

const DEFAULT_PPE_OPTIONS = [
  "A/C Cab",
  "Overalls",
  "Face Shield",
  "PVC Apron",
  "Respirator",
  "Goggles",
  "PVC Boots & Gloves"
];

const AgronomistAdmin = () => {
  const propertyId = useSelector(state => state.property.activePropertyId);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preparationPPE, setPreparationPPE] = useState([]);
  const [sprayingPPE, setSprayingPPE] = useState([]);
  // Environmental defaults — temperature and humidity carry min/max bounds
  // now (stakeholder feedback). Legacy singular `temperature` / `humidity`
  // saved by older versions are loaded into `*_min` so existing setups keep
  // a sensible value after upgrade; `rain` represents the maximum allowable
  // rain per hour (relabelled in the UI).
  const [envDefaults, setEnvDefaults] = useState({
    wind_min: null,
    wind_max: null,
    temperature_min: null,
    temperature_max: null,
    humidity_min: null,
    humidity_max: null,
    delta_t_min: null,
    delta_t_max: null,
    rain: null,
  });
  const [requiredSections, setRequiredSections] = useState(mergeRequiredSections(null));

  useEffect(() => {
    if (propertyId) {
      loadSettings();
    }
  }, [propertyId]);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const res = await sprayService.getSettings(propertyId);
      if (res && res.settings) {
        const settings = res.settings;

        if (settings.ppe_defaults_preparation) {
          setPreparationPPE(settings.ppe_defaults_preparation.setting_value || []);
        }
        if (settings.ppe_defaults_spraying) {
          setSprayingPPE(settings.ppe_defaults_spraying.setting_value || []);
        }
        if (settings.environmental_defaults) {
          const raw = settings.environmental_defaults.setting_value || {};
          // Backward compat: older saves had singular `temperature` /
          // `humidity` values. Hoist them into `*_min` so the upgraded UI
          // shows a sensible bound; admin can fill the matching `*_max`.
          setEnvDefaults({
            wind_min: raw.wind_min ?? null,
            wind_max: raw.wind_max ?? null,
            temperature_min: raw.temperature_min ?? raw.temperature ?? null,
            temperature_max: raw.temperature_max ?? null,
            humidity_min: raw.humidity_min ?? raw.humidity ?? null,
            humidity_max: raw.humidity_max ?? null,
            // Delta T defaults — manual entry now (was Stull-derived
            // from humidity/temp). Older saves won't have them; they
            // load as null and the admin fills them in.
            delta_t_min: raw.delta_t_min ?? null,
            delta_t_max: raw.delta_t_max ?? null,
            rain: raw.rain ?? null,
          });
        }
        if (settings.required_confirmations) {
          setRequiredSections(mergeRequiredSections(settings.required_confirmations.setting_value));
        }
      }
    } catch (err) {
      console.error('Error loading spray settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSavePPE = async () => {
    setSaving(true);
    try {
      await Promise.all([
        sprayService.updateSetting({
          property_id: propertyId,
          setting_key: 'ppe_defaults_preparation',
          setting_value: preparationPPE,
        }),
        sprayService.updateSetting({
          property_id: propertyId,
          setting_key: 'ppe_defaults_spraying',
          setting_value: sprayingPPE,
        }),
      ]);
      message.success('PPE defaults saved successfully');
    } catch (err) {
      console.error('Error saving PPE defaults:', err);
      message.error('Failed to save PPE defaults');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEnvironmental = async () => {
    setSaving(true);
    try {
      await sprayService.updateSetting({
        property_id: propertyId,
        setting_key: 'environmental_defaults',
        setting_value: envDefaults,
      });
      message.success('Environmental limit defaults saved successfully');
    } catch (err) {
      console.error('Error saving environmental defaults:', err);
      message.error('Failed to save environmental defaults');
    } finally {
      setSaving(false);
    }
  };

  const updateEnvDefault = (field, value) => {
    setEnvDefaults(prev => ({ ...prev, [field]: value }));
  };

  const toggleRequired = (sectionId, required) => {
    setRequiredSections(prev => ({ ...prev, [sectionId]: required }));
  };

  const handleSaveRequiredSections = async () => {
    setSaving(true);
    try {
      // Explicit code-check on top of the throw-on-error contract from
      // sprayService.updateSetting — guards against a future API change that
      // might 200-with-failure rather than reject, so the success toast can
      // never lie about whether persistence happened.
      const res = await sprayService.updateSetting({
        property_id: propertyId,
        setting_key: 'required_confirmations',
        setting_value: requiredSections,
      });
      if (res?.code !== '0000') {
        throw new Error(res?.message || 'Unexpected response saving required confirmations');
      }
      message.success('Required confirmations saved');
    } catch (err) {
      console.error('Error saving required confirmations:', err);
      message.error('Failed to save required confirmations');
    } finally {
      setSaving(false);
    }
  };

  // Group toggle definitions by phase so the admin UI mirrors the order an
  // operator actually encounters them in (Prepare → Spray → Cleanup).
  const sectionsByPhase = SPRAY_REQUIRED_SECTIONS.reduce((acc, s) => {
    acc[s.phase] = acc[s.phase] || [];
    acc[s.phase].push(s);
    return acc;
  }, {});
  const phaseLabels = { prepare: 'Prepare', spray: 'Spray', cleanup: 'Cleanup' };


  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <AgronomistAdminWrapper>
      {/* PPE Defaults */}
      <div className="settings-section">
        <h3>PPE Defaults</h3>
        <p style={{ color: '#666', marginBottom: 16 }}>
          Set the default PPE requirements for new spray jobs. These will be pre-filled when creating a spray job but can be edited per job.
        </p>

        <Form layout="vertical">
          <Form.Item label="Default Preparation PPE">
            <Select
              mode="tags"
              style={{ width: '100%' }}
              placeholder="Select or enter preparation PPE defaults"
              value={preparationPPE}
              onChange={(value) => setPreparationPPE(value.filter(v => v && v.trim()))}
              size="large"
              notFoundContent={null}
            >
              {DEFAULT_PPE_OPTIONS.map(tag => (
                <Option key={tag} value={tag}>{tag}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Default Spraying PPE">
            <Select
              mode="tags"
              style={{ width: '100%' }}
              placeholder="Select or enter spraying PPE defaults"
              value={sprayingPPE}
              onChange={(value) => setSprayingPPE(value.filter(v => v && v.trim()))}
              size="large"
              notFoundContent={null}
            >
              {DEFAULT_PPE_OPTIONS.map(tag => (
                <Option key={tag} value={tag}>{tag}</Option>
              ))}
            </Select>
          </Form.Item>

          <Button
            type="primary"
            onClick={handleSavePPE}
            loading={saving}
            className="save-button"
          >
            Save PPE Defaults
          </Button>
        </Form>
      </div>

      {/* Environmental Limit Defaults */}
      <div className="settings-section">
        <h3>Environmental Limit Defaults</h3>
        <p style={{ color: '#666', marginBottom: 16 }}>
          Set the default environmental limits for new spray jobs. Enter temperature and humidity — Delta T will be calculated automatically.
        </p>

        <Form layout="vertical">
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Form.Item label="Wind Min (km/h)">
                <InputNumber
                  size="large"
                  min={0}
                  value={envDefaults.wind_min}
                  onChange={(v) => updateEnvDefault('wind_min', v)}
                  placeholder="Min"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item label="Wind Max (km/h)">
                <InputNumber
                  size="large"
                  min={0}
                  value={envDefaults.wind_max}
                  onChange={(v) => updateEnvDefault('wind_max', v)}
                  placeholder="Max"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Form.Item label="Temperature Min (°C)">
                <InputNumber
                  size="large"
                  value={envDefaults.temperature_min}
                  onChange={(v) => updateEnvDefault('temperature_min', v)}
                  placeholder="Min"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item label="Temperature Max (°C)">
                <InputNumber
                  size="large"
                  value={envDefaults.temperature_max}
                  onChange={(v) => updateEnvDefault('temperature_max', v)}
                  placeholder="Max"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Form.Item label="Humidity Min (%)">
                <InputNumber
                  size="large"
                  min={0}
                  max={100}
                  value={envDefaults.humidity_min}
                  onChange={(v) => updateEnvDefault('humidity_min', v)}
                  placeholder="Min"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item label="Humidity Max (%)">
                <InputNumber
                  size="large"
                  min={0}
                  max={100}
                  value={envDefaults.humidity_max}
                  onChange={(v) => updateEnvDefault('humidity_max', v)}
                  placeholder="Max"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item label="Delta T Min (°C)">
                <InputNumber
                  size="large"
                  min={0}
                  value={envDefaults.delta_t_min}
                  onChange={(v) => updateEnvDefault('delta_t_min', v)}
                  placeholder="Min"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item label="Delta T Max (°C)">
                <InputNumber
                  size="large"
                  min={0}
                  value={envDefaults.delta_t_max}
                  onChange={(v) => updateEnvDefault('delta_t_max', v)}
                  placeholder="Max"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Form.Item label="Max Rain (mm/hr)" extra="Maximum allowable rainfall during spray jobs.">
                <InputNumber
                  size="large"
                  min={0}
                  value={envDefaults.rain}
                  onChange={(v) => updateEnvDefault('rain', v)}
                  placeholder="Max rain per hour"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Button
            type="primary"
            onClick={handleSaveEnvironmental}
            loading={saving}
            className="save-button"
          >
            Save Environmental Defaults
          </Button>
        </Form>
      </div>

      {/* Required Confirmations */}
      <div className="settings-section">
        <h3>Required Confirmations</h3>
        <p style={{ color: '#666', marginBottom: 16 }}>
          Toggle which confirmations the operator must complete before
          progressing each step of a spray job. Disable an item if your
          property handles that check outside the app.
        </p>

        {['prepare', 'spray', 'cleanup'].map((phase) => (
          <div key={phase} style={{ marginBottom: 20 }}>
            <Divider orientation="left" style={{ margin: '8px 0 12px' }}>
              {phaseLabels[phase]} step
            </Divider>
            {(sectionsByPhase[phase] || []).map((section) => (
              <Row
                key={section.id}
                gutter={[16, 8]}
                align="middle"
                style={{ marginBottom: 8 }}
              >
                <Col flex="auto">{section.label}</Col>
                <Col>
                  <Switch
                    checked={!!requiredSections[section.id]}
                    onChange={(checked) => toggleRequired(section.id, checked)}
                    checkedChildren="Required"
                    unCheckedChildren="Optional"
                  />
                </Col>
              </Row>
            ))}
          </div>
        ))}

        <Button
          type="primary"
          onClick={handleSaveRequiredSections}
          loading={saving}
          className="save-button"
        >
          Save Required Confirmations
        </Button>
      </div>
    </AgronomistAdminWrapper>
  );
};

export default AgronomistAdmin;
